import { useState, useRef } from 'react';
import { Modal, Form, Table, Input, message, Row, Col } from 'antd';
import {
  EllipsisOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { errorMessage } from '../../../../helpers/errorMessageFunc';
import { getVerificationToken } from '../../../../components/formItems/verificationCode';
import { toPrice } from '../../../../helpers/stringFormat';
import { backendPostReturnPromise } from '../../../../helpers/callBackend';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';

const statusMap = {
  inProgress: <EllipsisOutlined style={{ fontSize: '18px' }} />,
  notStarted: '',
  pending: <LoadingOutlined style={{ fontSize: '18px' }} />,
  success: <CheckCircleOutlined style={{ color: 'green', fontSize: '18px' }} />,
  error: (
    <ExclamationCircleOutlined style={{ color: 'red', fontSize: '18px' }} />
  ),
};

const ReviewPayments = (props) => {
  const [displayData, setDisplayData] = useState(
    props.displayData.map((payment, idx) => {
      if (idx === 0) {
        return {
          ...payment,
          status: 'inProgress',
        };
      } else {
        return {
          ...payment,
          status: 'notStarted',
        };
      }
    })
  );
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());
  const [verificationCode, setVerificationCode] = useState();
  const [verificationToken, setVerificationtoken] = useState();
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  const getNewToken = async (index = 0) => {
    if (!displayData[0].sendingAccount) {
      errorMessage("Please select a 'From' bank account first.");
      return;
    }

    try {
      let res = await getVerificationToken(displayData[index]?.sendingAccount.id);
      if (!res) {
        return
      }
      setVerificationCode(null);
      setVerificationtoken(res);
      message.success("Code sent to your phone number")
    } catch (error) {
      return
    }

    // if (formRef.current) {
    //     formRef.current.setFieldsValue({
    //         ["verificationCode-" + index]: null,
    //     });
    // }
  };

  const handleChange = (e) => {
    setVerificationCode(e.target.value);
  };

  const handlePayment = () => {
    const paymentType = props.paymentType;
    const payments = displayData.slice(0, -1);

    let insufficientFunds = false;
    let missingVerificationCode = false;

    payments.forEach((payment, index) => {
      if (payment.sendingAccount.balance < payment.amount) {
        insufficientFunds = true;
      }
    });
    if (insufficientFunds) {
      errorMessage('Insufficient funds. Amount exceeds the available balance.');
      return;
    }
    if (!verificationCode) {
      errorMessage('Please enter a verification code first.');
      return;
    }

    if (paymentType === 'external') {
      const formData = {
        paymentCount: payments.length,
        verification_code: verificationCode || ' ',
        verification_token: verificationToken || ' ',
        payments: {},
      };
      payments.forEach((payment, index) => {
        formData[`sendingAccount${index + 1}`] = payment.sendingAccount.id;
        formData[`recipient${index + 1}`] = payment.recipient.id;
        formData[`amount${index + 1}`] = payment.amount;
        formData[`description${index + 1}`] = payment.description || ' ';
        formData[`addenda${index + 1}`] = payment.addenda || ' ';
        formData[`method${index + 1}`] = payment.method;
        formData[`idempotencyKey${index + 1}`] = idempotencyKey + '-' + index;
      });
      setSubmitting(true);
      backendPostReturnPromise('/api/payments/multi/', formData)
        .then((response) => {
          setSubmitting(false);
          if (response.status === 200) {
            message.success("Payments submitted. Please see receipts tab for details.", 5);
            props.updateReceiptsTable(response.data.successful_payments, response.data.failed_payments);
            props.closeReviewModal();
            props.resetPaymentForm();
          }
        })
        .catch((error) => {
          console.error('err: ', error);
          let errMsg = error.response.data.description;
          if (errMsg === undefined) {
            errMsg = error.response.data.detail;
          }
          errorMessage(`Error [${error.response.status}]: ${errMsg}`, 0);
          props.updateReceiptsTable(error.response.data.successful_payments, error.response.data.failed_payments);
        });
    } else if (paymentType === 'internal') {
      const formData = {
        transferCount: payments.length,
        verification_code: verificationCode || ' ',
        verification_token: verificationToken || ' ',
        transfers: {},
      };
      payments.forEach((payment, index) => {
        formData[`sendingAccount${index + 1}`] = payment.sendingAccount.id;
        formData[`receivingAccount${index + 1}`] = payment.receivingAccount.id;
        formData[`amount${index + 1}`] = payment.amount;
        formData[`description${index + 1}`] = payment.description || ' ';
        formData[`idempotencyKey${index + 1}`] = idempotencyKey + '-' + index;
      });
      setSubmitting(true);
      backendPostReturnPromise('/api/payments/book-transfer/multi/', formData)
        .then((response) => {
          setSubmitting(false);
          if (response.status === 200) {
            message.success("Payments submitted. Please see receipts tab for details.", 5);
            props.updateReceiptsTable(response.data.successful_payments, response.data.failed_payments);
            props.closeReviewModal();
            props.resetPaymentForm();
          }
        })
        .catch((error) => {
          console.error('error: ', error);
          let errMsg = error.response.data.description;
          if (errMsg === undefined) {
            errMsg = error.response.data.detail;
          }
          errorMessage(`Error [${error.response.status}]: ${errMsg}`, 0);
          props.updateReceiptsTable(error.response.data.successful_payments, error.response.data.failed_payments);
        });
    }
  };

  const recipientKey = props.paymentType === 'external' ? 'recipient' : 'receivingAccount'
  const displayHeader = [
    {
      title: 'Sending Account',
      dataIndex: 'sendingAccount',
      render(text, record) {
        if (record.method === 'Total') {
          return <span></span>;
        } else {
          return <span>{text.description}</span>;
        }
      },
    },
    {
      title: recipientKey === 'recipient' ? 'Recipient' : 'Receiving Account',
      dataIndex: recipientKey,
      render(text, record) {
        if (record.method === 'Total') {
          return props.paymentType === 'external' ? <span></span> : 'Total';
        } else {
          return <span>{text.description}</span>;
        }
      },
    },
    ...(props.paymentType === 'external' ? [{
      title: 'Method',
      dataIndex: 'method',
    }] : []),
    {
      title: 'Description',
      dataIndex: 'description',
      render: (text, record) => {
        return (
          <Col style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column' }}>
            <Row> {record.description} </Row>
            {(props.paymentType === 'external' && record.method === 'ach') && <Row> Addenda: {record.addenda} </Row>}
          </Col>
        )
      }
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      align: 'right',
      render: (text) => <span>{toPrice(text)}</span>,
    },
  ];


  return (
    <Modal
      title="Review Payment"
      width={1000}
      visible={props.show}
      footer={null}
      onCancel={props.closeReviewModal}
    >
      <Form
        ref={formRef}
        layout="vertical"
        name="basic"
        autoComplete="off"
      >
        <Table columns={displayHeader} dataSource={displayData} pagination={false} />
        <Row style={{ marginTop: 20, width: "100%" }}>
          <Col span={18}>
            <Form.Item
              name="verificationCode"
              label="Verification Code"
              rules={[
                { required: true, message: 'Please input the 6-digit code sent to your phone.' },
              ]}
              onChange={handleChange}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Button
              onClick={getNewToken}
              style={{ marginLeft: 20, marginTop: 30 }}
              sx={{ fontSize: 14, textTransform: 'none', width: '200px' }} disableElevation fullWidth size="small" type="submit" variant="contained" color="primary"
            >
              Generate New Code
            </Button>
          </Col>
        </Row>
        <Form.Item>
          <LoadingButton
            onClick={handlePayment}
            htmlType="submit"
            loading={submitting}
            loadingPosition="start"
            style={{ marginLeft: "30%", marginRight: "30%", marginTop: 30, width: 400 }}
            sx={{ fontSize: 14, textTransform: 'none' }} disableElevation fullWidth size="small" type="submit" variant="contained" color="primary"
          >
            Submit
          </LoadingButton>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ReviewPayments;
